import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import { cn } from "@v2/lib/utils";
import { EmptyState, SectionLoading } from "../../components/state";
import { supabaseV2 } from "../../lib/supabase";

export function OpponentsManager({ teamId }: { teamId: string }) {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const opponentsQuery = useQuery({
    queryKey: ["opponents", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("opponents")
        .select("id, name, short_name, notes")
        .eq("team_id", teamId)
        .order("name");
      if (error) throw error;
      return (data || []) as Array<{ id: string; name: string; short_name: string | null; notes: string | null }>;
    },
  });
  const create = useMutation({
    mutationFn: async () => {
      if (!name.trim()) return;
      const { error } = await supabaseV2.from("opponents").insert({
        team_id: teamId,
        name: name.trim(),
        short_name: shortName.trim() || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setName("");
      setShortName("");
      queryClient.invalidateQueries({ queryKey: ["opponents", teamId] });
    },
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabaseV2.from("opponents").delete().eq("id", id).eq("team_id", teamId);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["opponents", teamId] }),
  });
  const opponents = opponentsQuery.data || [];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t("opponents.cardTitle")}</CardTitle>
        <CardDescription>{t("opponents.cardDesc", { count: opponents.length })}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <form
          className="flex flex-wrap gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("opponents.namePh")} required className="h-8 text-sm flex-1 min-w-[160px]" />
          <Input value={shortName} onChange={(e) => setShortName(e.target.value)} placeholder={t("opponents.shortPh")} className="h-8 text-sm w-28" />
          <Button type="submit" size="sm" className="h-8" disabled={create.isPending || !name.trim()}>
            <Plus className="h-3 w-3" /> {t("opponents.add")}
          </Button>
        </form>
        {opponentsQuery.isLoading && <SectionLoading />}
        {!opponentsQuery.isLoading && opponents.length === 0 && (
          <EmptyState text={t("opponents.empty")} />
        )}
        {opponents.length > 0 && (
          <div className="rounded-2xl bg-background overflow-hidden max-h-72 overflow-y-auto">
            {opponents.map((opp, idx) => (
              <div
                key={opp.id}
                className={cn("flex items-center gap-2 px-3 py-2 text-sm", idx > 0 && "border-t")}
              >
                <span className="flex-1 min-w-0 truncate">{opp.name}</span>
                {opp.short_name && (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {opp.short_name}
                  </span>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-rose-600 hover:bg-rose-50"
                  onClick={() => {
                    if (window.confirm(t("opponents.deleteConfirm", { name: opp.name }))) remove.mutate(opp.id);
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
